/*
- applyConstraints for asr
- ondevicechange not triggered for headphones at least on linux

SAFARI
- ios safari has some issues, when getusermedia again the previous one is muted permanently https://bugs.webkit.org/show_bug.cgi?id=179363
- groupId is absent

CHROME
- before permissions, enumerateDevices returns blank ids and labels
- before permissions, only the first device of each kind is returned
- some devices have as id and label the 'default' value, which is duplicate of some other device in the list

FIREFOX
- before permissions, all devices are returned but without labels
- after, some inputs may have same id but different labels

globally
- sync and store list of ids/labels used
- device used is unplugged
- device is plugged back
- new device plugged

per mediastream
- add/pause/remove effect on media
- set more than one sinks
*/
import { Events } from "@auvious/utils";
import { InputEffect } from "./input";
import { SinkEffect } from "./sink";
import { log, stopStream } from "./utils";
export class MediaPipe {
    constructor() {
        this.events = new Events();
        this.unusedTracks = new Set();
        this.effects = [];
        this.setup();
    }
    get streamIn() {
        return this.effects[0].stream;
    }
    get streamOut() {
        return this.effects[this.effects.length - 1].stream;
    }
    addEffect(effect) {
        const offOutput = effect.onOutput((stream) => this.handleOutput(effect, stream));
        const offUnload = effect.onUnload(() => this.removeEffect(effect));
        // sink always last
        this.effects.push(this.effects[this.effects.length - 1]);
        this.effects[this.effects.length - 2] = {
            effect,
            unsubscribe() {
                offOutput();
                offUnload();
            },
        };
        const previous = this.effects[this.effects.length - 3];
        if (previous === null || previous === void 0 ? void 0 : previous.stream) {
            log("piping", previous.effect.name, "to", effect.name);
            effect.setInput(previous.stream);
        }
        else {
            log(effect.name, "no streams to pipe to effect");
        }
    }
    removeEffect(effect) {
        const index = this.getMetadataIndex(effect);
        if (index === -1) {
            return;
        }
        const metadata = this.effects[index];
        const next = this.effects[index + 1];
        const previous = this.effects[index - 1];
        if (metadata) {
            metadata.unsubscribe();
            this.deleteMetadata(effect);
        }
        if (previous.stream) {
            next.effect.setInput(previous.stream);
        }
    }
    reset() {
        this.effects.forEach((md) => {
            md.unsubscribe();
            stopStream(md.stream, true, true);
        });
        this.unusedTracks.forEach((track) => track.stop());
        this.unusedTracks = new Set();
        this.effects = [];
        this.setup();
    }
    setup() {
        this.input = new InputEffect();
        this.sink = new SinkEffect();
        this.effects.push({
            effect: this.input,
            unsubscribe: () => { },
        });
        this.effects.push({
            effect: this.sink,
            unsubscribe: () => { },
        });
        this.input.onOutput((stream) => this.handleOutput(this.input, stream));
        this.sink.onOutput((stream) => this.handleOutput(this.sink, stream));
    }
    handleOutput(effect, stream) {
        const index = this.getMetadataIndex(effect);
        if (index === -1) {
            log("effect not found on handleOutput");
            return;
        }
        const metadata = this.effects[index];
        const next = this.effects[index + 1];
        if (!this.outputChanged(metadata.stream, stream)) {
            return;
        }
        if (metadata.stream) {
            metadata.stream
                .getTracks()
                .forEach((track) => this.unusedTracks.add(track));
        }
        // isolate streams from downstream
        metadata.stream = new MediaStream(stream);
        if (next) {
            log(effect, "will pipe stream ", metadata.stream, "to next effect", next.effect);
            next.effect.setInput(metadata.stream);
        }
        else {
            this.events.emit("output", metadata.stream);
            this.stopUnusedTracks();
        }
    }
    stopUnusedTracks() {
        const activeTracks = new Set(this.effects.map((md) => { var _a; return (_a = md.stream) === null || _a === void 0 ? void 0 : _a.getTracks(); }).flat());
        log("active tracks", activeTracks);
        this.unusedTracks.forEach((track) => {
            if (!activeTracks.has(track)) {
                log("stopping unused track", track);
                track.stop();
            }
        });
        this.unusedTracks.clear();
    }
    outputChanged(a, b) {
        return ((a === null || a === void 0 ? void 0 : a.getAudioTracks()[0]) !== (b === null || b === void 0 ? void 0 : b.getAudioTracks()[0]) ||
            (a === null || a === void 0 ? void 0 : a.getVideoTracks()[0]) !== (b === null || b === void 0 ? void 0 : b.getVideoTracks()[0]));
    }
    getMetadataIndex(effect) {
        return this.effects.findIndex((md) => md.effect === effect);
    }
    deleteMetadata(effect) {
        this.effects = this.effects.filter((md) => md.effect !== effect);
    }
}
